import api from "./axiosConfig";

export interface AffiliateDto {
  id: string;
  tenantId: string;
  userId?: string | null;
  clientId?: string | null;
  name: string;
  email: string;
  phone: string;
  referralCode: string;
  referralLink: string;
  tier: string;
  commissionRate: number;
  commissionType: string;
  oneTimeBonusAmount?: number | null;
  status: string;
  payoutMethod?: string | null;
  cookieDurationDays: number;
  minPayoutAmount: number;
  totalEarned: number;
  totalPaid: number;
  pendingBalance: number;
  totalReferrals: number;
  activeReferrals: number;
  createdAt: string;
  lastReferralAt?: string | null;
}

export interface ReferralDto {
  id: string;
  tenantId: string;
  affiliateId: string;
  affiliateName: string;
  affiliateTier: string;
  clientId?: string | null;
  clientName: string;
  clientEmail: string;
  referralCode: string;
  referredEntityType: string;
  referredName: string;
  referredEmail: string;
  status: string;
  commissionAmount: number;
  totalCommissionEarned: number;
  paidAt?: string | null;
  convertedAt?: string | null;
  createdAt: string;
}

export interface CommissionSummaryDto {
  affiliateCount: number;
  activeAffiliateCount: number;
  tierExternalCount: number;
  tierOperatorCount: number;
  tierClientCount: number;
  pendingReferralCount: number;
  enrolledReferralCount: number;
  paidReferralCount: number;
  convertedReferralCount: number;
  pendingPipelineAmount: number;
  totalPaidOut: number;
  operatorProgram?: OperatorReferralProgramDto | null;
}

export interface OperatorReferralProgramDto {
  affiliateId: string;
  referralCode: string;
  referralLink: string;
  totalReferrals: number;
  activeReferrals: number;
  convertedReferrals: number;
  pendingBalance: number;
  totalEarned: number;
  milestoneProgress: number;
  milestoneTarget: number;
}

export interface ClientReferralSettingsDto {
  clientReferralsEnabled: boolean;
  clientReferrerRewardAmount: number;
  clientReferrerRewardType: string;
  clientNewClientRewardAmount: number;
  clientNewClientRewardType: string;
  clientPreviewSummary: string;
}

export interface CommissionLedgerEntryDto {
  id: string;
  tenantId: string;
  affiliateId: string;
  affiliateName: string;
  referralId: string;
  referralSummary?: string | null;
  amount: number;
  type: string;
  status: string;
  description?: string | null;
  earnedAt: string;
  paidAt?: string | null;
  payoutReference?: string | null;
}

export interface CreateAffiliateRequest {
  name: string;
  email: string;
  phone: string;
  tier: string;
  commissionRate: number;
  commissionType?: string;
  oneTimeBonusAmount?: number | null;
  referralCode?: string;
}

export interface UpdateAffiliateRequest {
  name?: string;
  email?: string;
  phone?: string;
  commissionRate?: number;
  commissionType?: string;
  oneTimeBonusAmount?: number | null;
  status?: string;
  payoutMethod?: string;
  cookieDurationDays?: number;
  minPayoutAmount?: number;
}

export interface UpdateClientReferralSettingsRequest {
  clientReferralsEnabled?: boolean;
  clientReferrerRewardAmount?: number;
  clientReferrerRewardType?: string;
  clientNewClientRewardAmount?: number;
  clientNewClientRewardType?: string;
}

export interface CommissionCalculationResultDto {
  referralsUpdated: number;
  totalCommissionCalculated: number;
  ledgerEntriesCreated: number;
}

export interface CommissionBatchPayoutResultDto {
  entriesPaid: number;
  totalAmount: number;
}

export const affiliateApi = {
  list: (tier?: string) =>
    api.get<AffiliateDto[]>("/affiliates", { params: tier ? { tier } : {} }).then((r) => r.data),
  summary: () => api.get<CommissionSummaryDto>("/affiliates/commission-summary").then((r) => r.data),
  operatorProgram: () => api.get<OperatorReferralProgramDto>("/affiliates/operator-program").then((r) => r.data),
  clientReferralSettings: () => api.get<ClientReferralSettingsDto>("/affiliates/client-referral-settings").then((r) => r.data),
  updateClientReferralSettings: (body: UpdateClientReferralSettingsRequest) =>
    api.put<ClientReferralSettingsDto>("/affiliates/client-referral-settings", body).then((r) => r.data),
  referrals: (tier?: string, status?: string) =>
    api.get<ReferralDto[]>("/affiliates/referrals", { params: { tier, status } }).then((r) => r.data),
  create: (body: CreateAffiliateRequest) => api.post<AffiliateDto>("/affiliates", body).then((r) => r.data),
  update: (id: string, body: UpdateAffiliateRequest) =>
    api.put<AffiliateDto>(`/affiliates/${id}`, body).then((r) => r.data),
  delete: (id: string) => api.delete(`/affiliates/${id}`),
  payout: (id: string, amount?: number) =>
    api.post(`/affiliates/${id}/payout`, { amount }).then((r) => r.data),
  calculateCommissions: () =>
    api.post<CommissionCalculationResultDto>("/affiliates/calculate-commissions").then((r) => r.data),
  markPaid: (referralId: string) =>
    api.post<ReferralDto>(`/affiliates/referrals/${referralId}/mark-paid`).then((r) => r.data),
  commissionsList: () => api.get<CommissionLedgerEntryDto[]>("/commissions").then((r) => r.data),
  commissionsCalculate: () =>
    api.post<CommissionCalculationResultDto>("/commissions/calculate").then((r) => r.data),
  commissionsApprove: (id: string) => api.post(`/commissions/${id}/approve`).then((r) => r.data),
  commissionsPayout: () =>
    api.post<CommissionBatchPayoutResultDto>("/commissions/payout").then((r) => r.data),
  validateCode: (code: string) =>
    api.get<{ valid: boolean; tier?: string; affiliateName?: string; subdomain?: string }>(
      `/referrals/validate/${encodeURIComponent(code)}`,
    ).then((r) => r.data),
};
